import { FC, MouseEventHandler, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Box,
	FormControl,
	FormLabel,
	Radio,
	RadioGroup,
	Typography,
	useTheme,
} from '@mui/material';

import { PaymentMethodType } from '@mopla/data-models';
import { CreditCardIcon, MplButton, SepaIcon } from '@mopla/ui';

import { Label } from './PaymentMethodSelectionStep.styles';

interface IProps {
	handleClick: (selectedPaymentMethod: PaymentMethodType) => void;
	handleOnClose?: VoidFunction;
}
interface IRadioLabelProps {
	text: string;
	icon: ReactElement;
}

const RadioLabel: FC<IRadioLabelProps> = ({ text, icon }): ReactElement => {
	return (
		<Box
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			width="100%"
		>
			<Typography variant="body1">{text}</Typography>
			<Box sx={{ marginRight: '9px' }}>{icon}</Box>
		</Box>
	);
};

export const PaymentMethodSelectionStep: FC<IProps> = ({
	handleClick,
	handleOnClose,
}) => {
	const { t } = useTranslation('payment');
	const theme = useTheme();

	const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('sepa');

	const handleSelection: MouseEventHandler = () => {
		handleClick(paymentMethod);
	};

	return (
		<FormControl fullWidth>
			<Typography variant="h2">{t('add_payment_method')}</Typography>

			<FormLabel sx={{ marginTop: '16px' }} id="payment-methods-label">
				<Typography variant="body1" color={theme.palette.neutral[100]}>
					{t('select_payment_flow')}
				</Typography>
			</FormLabel>
			<RadioGroup
				sx={{ mt: 4 }}
				aria-labelledby="payment-methods-label"
				defaultValue="sepa"
				name="payment-methods"
			>
				<Label
					data-testid="PaymentMethodSelectionStep-sepaOption"
					value="sepa"
					control={
						<Radio
							onChange={(e) =>
								setPaymentMethod(e.target.value as PaymentMethodType)
							}
						/>
					}
					label={<RadioLabel text={t('sepa')} icon={<SepaIcon />} />}
				/>
				<Label
					data-testid="PaymentMethodSelectionStep-otherOption"
					value="other"
					control={
						<Radio
							onChange={(e) =>
								setPaymentMethod(e.target.value as PaymentMethodType)
							}
						/>
					}
					label={
						<RadioLabel
							text={t('another_payment_method')}
							icon={<CreditCardIcon />}
						/>
					}
				/>
			</RadioGroup>

			<MplButton
				testId="PaymentMethodSelectionStep-nextButton"
				sx={{ marginTop: '24px' }}
				onClick={handleSelection}
			>
				{t('button.next')}
			</MplButton>
			<MplButton
				testId="PaymentMethodSelectionStep-cancelButton"
				variant="outlined"
				sx={{ marginTop: '16px' }}
				onClick={handleOnClose}
			>
				{t('edit_modal.button.cancel')}
			</MplButton>
		</FormControl>
	);
};
